import { useEffect, createContext, useContext, useState, useMemo } from 'react';
import axios from 'axios';
import { get, sortBy } from 'lodash';
import { createConsumer } from '@rails/actioncable';
import { toast } from 'sonner';
import { Toaster } from '@components';
import { downloadBinary } from '../utils/files';
import { resolveDeadline } from '../utils/deadlines';

export function searchWithAccentsAndPunctuation(input, searchTerm) {
  // Normalize input: Remove diacritics and normalize punctuation
  const normalizeString = str =>
    (str || '')
      .normalize('NFD') // Decompose accents
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/['"’`~^]/g, '') // Remove apostrophes/punctuation
      .replace(/[^\w\s]/g, ' ') // Replace other non-alphanumeric characters with space
      .toLowerCase() // Convert to lowercase
      .trim(); // Remove extra spaces

  // Normalize and split into searchable words
  const normalizedInput = normalizeString(input);
  const normalizedSearchTerm = normalizeString(searchTerm);

  // Split both normalized input and search term into words and check if all words match
  const searchWords = normalizedSearchTerm.split(/\s+/); // Split by spaces
  return searchWords.every(word => normalizedInput.includes(word));
}

const gridCols =
  'grid-cols-[2fr_1fr_1fr] lg:grid-cols-[56px_3.5fr_71px_80px_130px_88px_100px_100px_0.5fr]';

interface SortInterface {
  id: number;
  position: number;
}

export const DealContext = createContext();

export const useDeal = () => {
  const context = useContext(DealContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a DealProvider');
  }

  return context;
};

let refetchTimeout;

const channelConsumer = createConsumer();

const subscribe = callback => {
  return channelConsumer.subscriptions.create(
    { channel: 'UserChannel' },
    {
      received(data) {
        clearTimeout(refetchTimeout);
        refetchTimeout = setTimeout(() => callback(data), 500);
      }
    }
  );
};

const storage = {
  get: key => {
    const data = localStorage.getItem(key);
    if (data) {
      return JSON.parse(data);
    }
  },
  write: (key, ids = []) => {
    localStorage.setItem(key, JSON.stringify(ids));
  },
  del: key => {
    localStorage.deleteItem(key);
  }
};

export const DealProvider = ({ dealId, children }) => {
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deal, setDeal] = useState();
  const [dealMembers, setDealMembers] = useState([]);
  const [taskItemsGroups, setTaskItemsGroups] = useState([]);
  const [taskGroupById, setTaskGroupById] = useState({});
  const [taskItemById, setTaskItemById] = useState({});
  const [availableStates, setAvailableStates] = useState([]);
  const [authStatuses, setAuthStatuses] = useState({});
  const [dealObjects, setDealObjects] = useState({});
  const [currentTaskItemFilter, setCurrentTaskItemFilter] = useState<'INTERNAL' | 'PUBLIC'>(
    'PUBLIC'
  );
  const [addModalIsOepned, setAddModalIsOpened] = useState(false);

  const [selectedEditors, setSelectedEditors] = useState([]);
  const [selectedViewers, setSelectedViewers] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedDeadline, setSelectedDeadline] = useState();
  const filters = {
    selectedEditors,
    setSelectedEditors,
    selectedViewers,
    setSelectedViewers,
    selectedStatuses,
    setSelectedStatuses,
    selectedDeadline,
    setSelectedDeadline
  };

  // Portal id for dropdown
  const portalId = `deals-${dealId}`;

  // GROUPS
  const groupsStorageKey = `deal-groups-${dealId}`;
  const [closedGroupIds, setClosedGroupIds] = useState([]);

  const toggleGroup = groupId => {
    const storedGroupIds = storage.get(groupsStorageKey);
    let newGroupIds;
    if (Array.isArray(storedGroupIds) && storedGroupIds.includes(String(groupId))) {
      newGroupIds = storedGroupIds.filter(id => id !== String(groupId));
    } else {
      const safeArray = Array.isArray(storedGroupIds) ? storedGroupIds : [];
      newGroupIds = [...safeArray, String(groupId)];
    }
    storage.write(groupsStorageKey, newGroupIds);
    setClosedGroupIds(newGroupIds);
  };

  const toggleAllGroups = () => {
    const groupIds = Object.keys(taskGroupById);
    const storedGroupIds = storage.get(groupsStorageKey);

    if (Array.isArray(storedGroupIds) && storedGroupIds.length === groupIds.length) {
      storage.write(groupsStorageKey, []);
      setClosedGroupIds([]);
    } else {
      storage.write(groupsStorageKey, groupIds);
      setClosedGroupIds(groupIds);
    }
  };

  useEffect(() => {
    const storedClosedGroupIds = storage.get(groupsStorageKey);
    if (Array.isArray(storedClosedGroupIds) && storedClosedGroupIds.length > 0) {
      setClosedGroupIds(storedClosedGroupIds);
    }
  }, [taskGroupById, currentTaskItemFilter]);

  const fetchDealObjects = async () => {
    const { data } = await axios.get(`/shared/deals/${dealId}/objects`);
    setDealObjects(data);
    return data;
  };

  useEffect(() => {
    fetchDealObjects();
  }, [dealId]);

  const onChannelUpdate = (data = {}) => {
    const taskItemId = get(data, 'task_item_id');

    if (!!taskItemId && !loading) {
      fetchData();
    }

    const removeSelection = get(data, 'remove_selection');
    if (removeSelection) {
      setSelectedItemIds([]);
    }
  };

  // FILTERING
  const [keyword, setKeyword] = useState('');
  const entries = Object.entries(taskItemById);
  const searchableData = Object.entries(taskItemById).map(([id, item]) => ({
    id,
    label: item.label
  }));

  const searchedTaskItemIds = useMemo(() => {
    const searchTerm = keyword.toLowerCase().trim();

    if (!searchTerm || searchTerm === '') {
      return Object.keys(taskItemById);
    }

    const matchingEntries = searchableData
      .filter(node => searchWithAccentsAndPunctuation(node.label, keyword))
      .map(e => e.id);
    return matchingEntries;
  }, [taskItemById, keyword]);

  useEffect(() => {
    if (closedGroupIds.length > 0 && closedGroupIds.length === taskItemsGroups.length) {
      storage.write(groupsStorageKey, []);
      setClosedGroupIds([]);
    }
  }, [keyword]);

  // DATA
  async function fetchData() {
    if (loading) {
      return;
    }

    setLoading(true);

    const {
      data: {
        auth_statuses = {},
        available_states = [],
        colors = {},
        deal,
        groups = [],
        deal_members = []
      } = {}
    } = await axios.get(`/deals/${dealId}/task_groups`);
    setDeal(deal);
    setDealMembers(deal_members);
    setTaskItemsGroups(groups);
    setAvailableStates(available_states);
    setAuthStatuses(auth_statuses);

    setTaskItemById(
      groups.reduce((acc, g) => {
        g.task_items.forEach(ti => (acc[ti.id] = ti));
        return acc;
      }, {})
    );

    setTaskGroupById(
      groups.reduce((acc, g) => {
        acc[g.id] = g;
        return acc;
      }, {})
    );

    setLoading(false);
    setInitialLoading(false);
    // setSelectedItemIds([]);
  }

  async function sortGroups(sortable_items: SortInterface[], successMessage) {
    setRefreshing(true);
    await axios({
      method: 'put',
      url: `/deals/${dealId}/task_groups/sort`,
      data: { sortable_items }
    });
    await fetchData();
    setRefreshing(false);
  }

  async function updateGroup(groupId, task_group, successMessage) {
    setRefreshing(true);
    const { data } = await axios({
      method: 'put',
      url: `/deals/${dealId}/task_groups/${groupId}`,
      data: { task_group }
    });
    await fetchData();
    setRefreshing(false);
    !!successMessage && toast.success(successMessage);
  }

  async function deleteGroup(groupId, successMessage) {
    setRefreshing(true);
    const url = `/deals/${dealId}/task_groups/${groupId}`;
    await axios({ method: 'delete', url });
    await fetchData();
    setRefreshing(false);
    !!successMessage && toast.success(successMessage);
  }

  async function sortTaskItems(sortable_items: SortInterface[], successMessage) {
    setRefreshing(true);
    await axios({
      method: 'put',
      url: `/task_items/sort`,
      data: { sortable_items }
    });
    await fetchData();
    setRefreshing(false);
  }

  async function createTaskItem(group_id, label, successMessage) {
    setRefreshing(true);
    const { data } = await axios({
      method: 'post',
      url: `/task_items`,
      data: { task_item: { deal_id: dealId, label, group_id } }
    });
    await fetchData();
    setRefreshing(false);
    !!successMessage && toast.success(successMessage);
  }

  async function deleteTaskItem(taskItemId, successMessage) {
    setRefreshing(true);
    const { data } = await axios({
      method: 'delete',
      url: `/task_items/${taskItemId}`
    });
    await fetchData();
    setRefreshing(false);
    !!successMessage && toast.success(successMessage);
  }

  async function updateTaskItem(taskItemId, task_item, successMessage) {
    setRefreshing(true);
    const { data } = await axios({
      method: 'put',
      url: `/task_items/${taskItemId}`,
      data: { task_item }
    });
    await fetchData();
    setRefreshing(false);
    !!successMessage && toast.success(successMessage);
  }

  async function batchDeleteTaskItems({ deal_id, task_item_ids = [], successMessage }) {
    setRefreshing(true);
    const { data } = await axios({
      method: 'delete',
      url: `/task_items/batch_destroy`,
      data: { deal_id, task_item_ids }
    });
    await fetchData();
    setRefreshing(false);
    setSelectedItemIds([]);
    !!successMessage && toast.success(successMessage);
  }

  async function taskItemsExportEmail({ deal_id, task_item_ids = [], successMessage }) {
    setRefreshing(true);
    const { data } = await axios({
      method: 'post',
      url: '/task_items/export/email',
      data: { deal_id, task_item_ids }
    });
    await fetchData();
    setRefreshing(false);
    setSelectedItemIds([]);
    !!successMessage && toast.success(successMessage);
  }

  async function taskItemsDownloadZip({ deal_id, task_item_ids = [], successMessage }) {
    setRefreshing(true);
    const { data } = await axios({
      method: 'post',
      url: '/task_items/export/zip',
      data: { deal_id, task_item_ids },
      responseType: 'blob'
    });
    const timestamp = Math.floor(new Date().valueOf() / 1000);
    downloadBinary({
      data,
      filename: `export-${dealId}-${timestamp}.zip`,
      contentType: 'application/zip'
    });
    await fetchData();
    setRefreshing(false);
    setSelectedItemIds([]);
    !!successMessage && toast.success(successMessage);
  }

  async function batchUpdateEndAt({ ids = [], date, successMessage, onSuccess }) {
    setRefreshing(true);
    const { data } = await axios({
      method: 'post',
      url: `/deals/${dealId}/batch_update_end_at?end_at=${date}`,
      data: { task_item_ids: ids }
    });
    await fetchData();
    setRefreshing(false);
    setSelectedItemIds([]);
    !!successMessage && toast.success(successMessage);
    if (onSuccess) {
      onSuccess();
    }
  }

  async function batchUpdateState({ ids = [], template_status_id, successMessage, onSuccess }) {
    setRefreshing(true);
    const { data } = await axios({
      method: 'post',
      url: `/deals/${dealId}/batch_update_state?template_status_id=${template_status_id}`,
      data: { task_item_ids: ids }
    });
    await fetchData();
    setRefreshing(false);
    setSelectedItemIds([]);
    !!successMessage && toast.success(successMessage);
    if (onSuccess) {
      onSuccess();
    }
  }

  useEffect(() => {
    fetchData();

    const subscription = subscribe(onChannelUpdate);

    return () => {
      subscription.unsubscribe();
    };
  }, [dealId]);

  function filterEditorOrViewer(items) {
    if (!selectedEditors?.length && !selectedViewers?.length) {
      return items;
    }

    return items.filter(
      item =>
        (selectedEditors.length > 0 &&
          item.editors.some(editor => selectedEditors?.includes(editor.id))) ||
        (selectedViewers.length > 0 &&
          item.viewers.some(viewer => selectedViewers?.includes(viewer.id)))
    );
  }

  function filterByStatus(items = []) {
    if (!selectedStatuses?.length && !selectedStatuses?.length) {
      return items;
    }

    const newItems = items.filter(item => selectedStatuses.includes(item.template_status_id));
    return newItems;
  }

  function filterByDate(items = []) {
    if (!selectedDeadline || selectedDeadline === '') {
      return items;
    }
    return items.filter(
      item => resolveDeadline(item.end_at, item.state === 'VALIDATED') === selectedDeadline
    );
  }

  function applyTaskItemFilters(groupTaskItems = []) {
    let newGroupItems = groupTaskItems;
    newGroupItems = filterEditorOrViewer(newGroupItems);
    newGroupItems = filterByStatus(newGroupItems);
    newGroupItems = filterByDate(newGroupItems);
    return newGroupItems;
  }

  // Single source of truth for the task items a group displays, in sorted order.
  // Both the group-level <ReactSortable> `list` (index.tsx) and each group child
  // (taskItemsGroup.tsx) MUST derive visibility from this so the sortable's
  // `list` stays 1:1 with the rendered DOM nodes.
  function visibleTaskItemIdsForGroup(group) {
    return sortBy(applyTaskItemFilters(get(group, 'task_items', [])), 'position')
      .map(taskItem => taskItem.id)
      .filter(id => searchedTaskItemIds.includes(String(id)))
      .filter(Boolean);
  }

  const localStorageTabKey = `DEFAULT_TASK_GROUP_DISPLAY_${dealId}`;

  function setCurrentTaskItemFilterWithEffect(filter = null) {
    localStorage.setItem(localStorageTabKey, filter);
    setCurrentTaskItemFilter(filter);
  }

  useEffect(() => {
    const groups = Object.values(taskGroupById);

    if (groups.length >= 1) {
      const savedDefaultTaskGroupDisplay = localStorage.getItem(localStorageTabKey);
      const isIncluced = ['INTERNAL', 'PUBLIC'].includes(savedDefaultTaskGroupDisplay);

      if (!!savedDefaultTaskGroupDisplay && isIncluced) {
        return setCurrentTaskItemFilterWithEffect(savedDefaultTaskGroupDisplay);
      }

      const hasInternalGroups = Object.values(taskGroupById).find(g => g.internal);
      if (!savedDefaultTaskGroupDisplay && !!hasInternalGroups) {
        return setCurrentTaskItemFilterWithEffect('INTERNAL');
      }

      return setCurrentTaskItemFilterWithEffect('PUBLIC');
    }
  }, [taskGroupById, loading]);

  const value = {
    gridCols,
    dealId,
    selectedItemIds,
    setSelectedItemIds,
    initialLoading,
    loading,
    setLoading,
    deal,
    taskItemsGroups,
    setTaskItemsGroups,
    setTaskGroupById,
    taskGroupById,
    setTaskItemById,
    taskItemById,
    refreshing,
    availableStates,
    authStatuses,
    toggleGroup,
    toggleAllGroups,
    closedGroupIds,
    searchedTaskItemIds,
    keyword,
    setKeyword,
    dealObjects,
    portalId,
    applyTaskItemFilters,
    visibleTaskItemIdsForGroup,
    dealMembers,
    currentTaskItemFilter,
    setCurrentTaskItemFilter: setCurrentTaskItemFilterWithEffect,
    addModalIsOepned,
    setAddModalIsOpened,
    actions: {
      sortGroups,
      updateGroup,
      deleteGroup,
      sortTaskItems,
      createTaskItem,
      deleteTaskItem,
      updateTaskItem,
      taskItemsDownloadZip,
      taskItemsExportEmail,
      batchDeleteTaskItems,
      batchUpdateEndAt,
      batchUpdateState
    },
    filters
  };

  return (
    <DealContext.Provider value={value}>
      {children}
      <Toaster position="top-right" />
    </DealContext.Provider>
  );
};
